import { GlBadge, GlPopover, GlLink, GlSkeletonLoader, GlIcon, GlSprintf, GlButton, GlAvatarLabeled } from '@gitlab/ui';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { glEmojiTag } from '~/emoji';
import { createAlert } from '~/alert';
import { followUser, unfollowUser } from '~/rest_api';
import { isUserBusy } from '~/set_status_modal/utils';
import Tracking from '~/tracking';
import HelpIcon from '~/vue_shared/components/help_icon/help_icon.vue';
import { I18N_ERROR_FOLLOW, I18N_ERROR_UNFOLLOW, I18N_USER_BLOCKED, I18N_USER_BUSY, I18N_USER_LEARN, I18N_USER_FOLLOW, I18N_USER_UNFOLLOW, USER_POPOVER_DELAY } from './constants';
const MAX_SKELETON_LINES = 4;
export default {
    name: 'UserPopover',
    maxSkeletonLines: MAX_SKELETON_LINES,
    I18N_USER_BLOCKED,
    I18N_USER_BUSY,
    I18N_USER_LEARN,
    USER_POPOVER_DELAY,
    components: {
        GlBadge,
        GlIcon,
        GlLink,
        GlPopover,
        GlSkeletonLoader,
        GlSprintf,
        GlButton,
        GlAvatarLabeled,
        HelpIcon
    },
    directives: {
        SafeHtml
    },
    mixins: [
        Tracking.mixin()
    ],
    props: {
        target: {
            type: HTMLElement,
            required: true
        },
        user: {
            type: Object,
            required: true
        },
        placement: {
            type: String,
            required: false,
            default: 'top'
        },
        show: {
            type: Boolean,
            required: false,
            default: false
        },
        container: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: [
        'follow',
        'unfollow'
    ],
    data () {
        return {
            toggleFollowLoading: false
        };
    },
    computed: {
        statusHtml () {
            if (!this.user.status) {
                return '';
            }
            if (this.user.status.emoji && this.user.status.message_html) {
                return `${glEmojiTag(this.user.status.emoji)} ${this.user.status.message_html}`;
            }
            if (this.user.status.message_html) {
                return this.user.status.message_html;
            }
            if (this.user.status.emoji) {
                return glEmojiTag(this.user.status.emoji);
            }
            return '';
        },
        userCannotMerge () {
            return this.target.dataset.cannotMerge;
        },
        recommendationReason () {
            return this.target.dataset.recommendationReason;
        },
        recommendationReasonIconVariant () {
            return this.isBusy ? 'subtle' : 'info';
        },
        recommendationReasonIconName () {
            return this.isBusy ? 'status-waiting' : 'status-active';
        },
        userIsLoading () {
            return !this.user?.loaded;
        },
        availabilityStatus () {
            return this.user?.status?.availability || '';
        },
        isNotCurrentUser () {
            return !this.userIsLoading && this.user.username !== gon.current_username;
        },
        shouldRenderToggleFollowButton () {
            return this.isNotCurrentUser && typeof this.user?.isFollowed !== 'undefined';
        },
        toggleFollowButtonText () {
            if (this.toggleFollowLoading) return null;
            return this.user?.isFollowed ? I18N_USER_UNFOLLOW : I18N_USER_FOLLOW;
        },
        toggleFollowButtonVariant () {
            return this.user?.isFollowed ? 'default' : 'confirm';
        },
        hasPronouns () {
            return Boolean(this.user?.pronouns?.trim());
        },
        isBlocked () {
            return this.user?.state === 'blocked';
        },
        isBusy () {
            return isUserBusy(this.availabilityStatus);
        },
        username () {
            return `@${this.user?.username}`;
        },
        cssClasses () {
            const classList = [
                'user-popover',
                'gl-w-34',
                'gl-overflow-clip'
            ];
            if (this.userCannotMerge) {
                classList.push('user-popover-cannot-merge');
            }
            // Skip the recommendation accent when "Cannot merge" is shown so its
            // warning header background wins; both classes style .popover-header.
            if (this.recommendationReason && !this.userCannotMerge) {
                classList.push(this.isBusy ? 'user-popover-recommendation-busy' : 'user-popover-recommendation');
            }
            return classList;
        }
    },
    methods: {
        async toggleFollow () {
            if (this.user.isFollowed) {
                this.unfollow();
            } else {
                this.follow();
            }
        },
        async follow () {
            this.toggleFollowLoading = true;
            this.track('click_button', {
                label: 'follow_from_user_popover'
            });
            try {
                await followUser(this.user.id);
                this.$emit('follow');
            } catch (error) {
                const message = error.response?.data?.message || I18N_ERROR_FOLLOW;
                createAlert({
                    message,
                    error,
                    captureError: true
                });
            } finally{
                this.toggleFollowLoading = false;
            }
        },
        async unfollow () {
            this.toggleFollowLoading = true;
            this.track('click_button', {
                label: 'unfollow_from_user_popover'
            });
            try {
                await unfollowUser(this.user.id);
                this.$emit('unfollow');
            } catch (error) {
                createAlert({
                    message: I18N_ERROR_UNFOLLOW,
                    error,
                    captureError: true
                });
            } finally{
                this.toggleFollowLoading = false;
            }
        }
    },
    safeHtmlConfig: {
        ADD_TAGS: [
            'gl-emoji'
        ]
    }
};
